import { memo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@heltarchat/ui';
import { AlertTriangle } from 'lucide-react';
import { ZohoCustomerUnpaidBillsSummaryResponse } from '@heltarchat/shared';
import { getCustomerZohoBillsUnpaidSummary } from 'utils/apiCall/billing';
import { formatCurrency } from '../AdminDashboard/utils';

/**
 * App-wide banner shown on every authenticated page while the business has
 * unpaid SMB bills (PENDING + FAILED). Links to /settings/35 to view & pay.
 * Non-critical: any fetch failure is swallowed (the banner just doesn't show).
 */
export const BillingBanner = memo(() => {
  const navigate = useNavigate();
  const [summary, setSummary] =
    useState<ZohoCustomerUnpaidBillsSummaryResponse | null>(null);

  useEffect(() => {
    let active = true;
    getCustomerZohoBillsUnpaidSummary()
      .then(res => {
        if (active) setSummary(res);
      })
      .catch(() => {
        // Non-critical: leave the banner hidden on error.
      });
    return () => {
      active = false;
    };
  }, []);

  if (!summary?.hasUnpaid) return null;

  const sym = summary.currency === 'INR' ? '₹' : summary.currency;
  const label =
    summary.count > 1
      ? `You have ${summary.count} unpaid bills`
      : 'You have an unpaid bill';

  return (
    <div className='flex flex-shrink-0 items-center justify-center gap-3 px-4 py-2 bg-[var(--heltar-state-warning)]/15 border-b border-[var(--heltar-state-warning)]/30'>
      <AlertTriangle className='w-4 h-4 flex-shrink-0 text-[var(--heltar-state-warning)]' />
      <span className='text-xs sm:text-sm text-[var(--heltar-text-primary)]'>
        {label}:{' '}
        <span className='font-semibold'>
          {formatCurrency(summary.totalDue, sym)}
        </span>{' '}
        due
      </span>
      <Button
        variant='greenHeltar'
        size='sm'
        className='h-7 text-xs'
        onClick={() => navigate('/settings/35')}
      >
        View &amp; Pay
      </Button>
    </div>
  );
});

BillingBanner.displayName = 'BillingBanner';
